import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpIcon, SparklesIcon, XIcon } from 'lucide-react';
import { ConsultantMessage } from './ConsultantMessage';
import { TypingIndicator } from './TypingIndicator';
import { consultantIdentity } from '../../data/consultant';
import { brand } from '../../data/site';
import { usePrefersReducedMotion } from '../../hooks/useMedia';
import type { ChatMessage, ConsultantAction } from '../../lib/consultant/types';
type Props = {
  messages: ChatMessage[];
  typing: boolean;
  /** 0–1 progress through the project qualification brief; null when inactive. */
  briefProgress: number | null;
  onSend: (value: string) => void;
  onAction: (action: ConsultantAction) => void;
  onClose: () => void;
};
export function ConsultantPanel({
  messages,
  typing,
  briefProgress,
  onSend,
  onAction,
  onClose
}: Props) {
  const reduced = usePrefersReducedMotion();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTo({
      top: node.scrollHeight,
      behavior: reduced ? 'auto' : 'smooth'
    });
  }, [messages, typing, reduced]);
  useEffect(() => {
    const timer = window.setTimeout(() => inputRef.current?.focus(), 320);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value) return;
    setDraft('');
    onSend(value);
  };
  return <motion.div className="fixed inset-x-2 bottom-2 top-16 z-[80] sm:inset-x-auto sm:inset-y-0 sm:left-5 sm:my-auto sm:h-[min(660px,84vh)] sm:w-[400px]" initial={reduced ? {
    opacity: 0
  } : {
    opacity: 0,
    scale: 0.96,
    x: -14
  }} animate={reduced ? {
    opacity: 1
  } : {
    opacity: 1,
    scale: 1,
    x: 0
  }} exit={reduced ? {
    opacity: 0
  } : {
    opacity: 0,
    scale: 0.97,
    x: -14
  }} transition={{
    duration: reduced ? 0.15 : 0.42,
    ease: [0.16, 1, 0.3, 1]
  }} role="dialog" aria-label={`${consultantIdentity.name} — ${consultantIdentity.role}`} data-id="element-97" data-mp="1vqo2z672f70w">
      <div className="flex h-full w-full flex-col overflow-hidden rounded-3xl border border-line bg-surface2/85 backdrop-blur-2xl" style={{
      boxShadow: '0 30px 90px -28px rgb(5 17 242 / 0.45), 0 0 0 1px rgb(5 17 242 / 0.08)'
    }} data-id="element-98" data-mp="1xi60a9ftn71x">
        {/* Header */}
        <header className="relative flex items-center gap-3 border-b border-line px-4 py-3.5" data-id="element-99" data-mp="26kxdoes9ccw7">
          <span className="absolute inset-x-0 top-0 h-px" style={{
          background: 'linear-gradient(90deg, transparent, rgb(5 17 242 / 0.7), transparent)'
        }} aria-hidden="true" data-id="element-100" data-mp="2bfkstn1dfuy4" />
          <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-brand" data-id="element-101" data-mp="1b9h9hqtocf9u">
            <img src={brand.logo} alt="" className="h-6 w-6 object-contain" aria-hidden="true" data-id="element-102" data-mp="169uo2u3ozspy" />
          </span>
          <div className="min-w-0 flex-1" data-id="element-103" data-mp="11iw49kjhwchy">
            <p className="font-display text-sm font-bold uppercase leading-none tracking-tightest" data-id="element-104" data-mp="11hl19mxvj8rw">
              {consultantIdentity.name}
            </p>
            <p className="label mt-1.5 flex items-center gap-1.5 text-muted" data-id="element-105" data-mp="sj9bfn4u0f0i">
              <motion.span className="h-1.5 w-1.5 rounded-full bg-brand" animate={reduced ? undefined : {
              opacity: [1, 0.35, 1]
            }} transition={{
              duration: 2.2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} aria-hidden="true" data-id="element-106" data-mp="nk6b2fzsqwyv" />
              {consultantIdentity.role}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close Morvix AI" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line text-ink transition-colors hover:border-brand hover:text-brand" data-cursor="hover" data-id="element-107" data-mp="2cwqfno7davrc">
            <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-108" data-mp="262i4qx55kk2g" />
          </button>
        </header>

        {/* Qualification progress */}
        <AnimatePresence data-id="element-109" data-mp="2b01i7uu10stc">
          {briefProgress !== null ? <motion.div className="border-b border-line px-4 py-2.5" initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-110" data-mp="1x9ejw3s6h4xs">
              <div className="flex items-center justify-between" data-id="element-111" data-mp="1sbwkb401w22g">
                <span className="label text-muted" data-id="element-112" data-mp="1ebc32c9saeb2">Project brief</span>
                <span className="label text-brand" data-id="element-113" data-mp="1ed6nzql226f5">{Math.round(briefProgress * 100)}%</span>
              </div>
              <div className="mt-2 h-px w-full bg-line" data-id="element-114" data-mp="1iei5f8trzlc9">
                <motion.div className="h-px bg-brand" animate={{
              width: `${Math.max(4, briefProgress * 100)}%`
            }} transition={{
              duration: 0.4,
              ease: [0.16, 1, 0.3, 1]
            }} data-id="element-115" data-mp="zp6cj1n7ff6k" />
              </div>
            </motion.div> : null}
        </AnimatePresence>

        {/* Conversation */}
        <div ref={scrollRef} className="flex-1 space-y-5 overflow-y-auto px-4 py-5" aria-live="polite" aria-atomic="false" data-id="element-116" data-mp="1imzhe9he1z6l">
          {messages.map(message => <ConsultantMessage key={message.id} message={message} onAction={onAction} data-id="element-117" data-mp="1rfsxua8kn4m1" />)}
          <AnimatePresence data-id="element-118" data-mp="1ndpj9cqlvjwj">
            {typing ? <motion.div initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.25
          }} data-id="element-119" data-mp="1ng28mayp0wjm">
                <TypingIndicator data-id="element-120" data-mp="u1hqn8kij6i3" />
              </motion.div> : null}
          </AnimatePresence>
        </div>

        {/* Composer */}
        <form onSubmit={submit} className="border-t border-line px-3 py-3" data-id="element-121" data-mp="y3cr6aumdfut">
          <div className="flex items-center gap-2 rounded-2xl border border-line bg-surface/70 px-3 py-2 transition-colors focus-within:border-brand" data-id="element-122" data-mp="131o44ug7o6h9">
            <SparklesIcon className="h-3.5 w-3.5 shrink-0 text-brand" aria-hidden="true" data-id="element-123" data-mp="17uh65akap0pn" />
            <label htmlFor="morvix-ai-input" className="sr-only" data-id="element-124" data-mp="1cttslu7985rp">
              Message Morvix AI
            </label>
            <input id="morvix-ai-input" ref={inputRef} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Ask about services, pricing, process…" autoComplete="off" className="min-w-0 flex-1 bg-transparent text-[13.5px] text-ink outline-none placeholder:text-muted" data-id="element-125" data-mp="1lse3waahjd5w" />
            <button type="submit" disabled={!draft.trim()} aria-label="Send message" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-opacity hover:bg-brand-deep disabled:opacity-30" data-cursor="hover" data-id="element-126" data-mp="3kzcf3z6mauss">
              <ArrowUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-127" data-mp="36brc059qsvkv" />
            </button>
          </div>
          <p className="label mt-2.5 px-1 text-center text-muted/70" data-id="element-128" data-mp="7rhs6f1sumtm">
            {brand.name} · {consultantIdentity.status}
          </p>
        </form>
      </div>
    </motion.div>;
}